import { apiClient } from '@/api/client'
import type {
  AiMealAnalysisResponse,
  BodyProfile,
  BodyProfileUpdate,
  DailyNutrition,
  FoodSearchPage,
  FoodTemplate,
  FoodTemplateCreate,
  FoodTemplateFromMealItems,
  FoodTemplateRegenerate,
  KaloriShopList,
  MealItemHistoryPage,
  MealLog,
  MealLogCreate,
  MealLogUpdate,
  MealPlanCreate,
  MealPlanRead,
  MealPlanSuggestion,
  MealPlanSuggestRequest,
  MealRegenerateRequest,
  TemplateSortOrder,
} from '@/api/types/nutrition'

// ==============================================================================
// Body Profile
// ==============================================================================

export async function getBodyProfile(): Promise<BodyProfile> {
  const res = await apiClient.get<BodyProfile>('/nutrition/profile')
  return res.data
}

export async function updateBodyProfile(data: BodyProfileUpdate): Promise<BodyProfile> {
  const res = await apiClient.put<BodyProfile>('/nutrition/profile', data)
  return res.data
}

// ==============================================================================
// Meals
// ==============================================================================

export async function createMeal(data: MealLogCreate): Promise<MealLog> {
  const res = await apiClient.post<MealLog>('/nutrition/meals', data)
  return res.data
}

export async function updateMeal(mealId: number, data: MealLogUpdate): Promise<MealLog> {
  const res = await apiClient.patch<MealLog>(`/nutrition/meals/${mealId}`, data)
  return res.data
}

export async function deleteMeal(mealId: number): Promise<void> {
  await apiClient.delete(`/nutrition/meals/${mealId}`)
}

// ==============================================================================
// AI Analysis
// ==============================================================================

export async function analyzeMeal(mealId: number): Promise<MealLog> {
  const res = await apiClient.post<MealLog>(`/nutrition/meals/${mealId}/analyze`)
  return res.data
}

export async function analyzeMealWithImage(mealId: number, files: File[]): Promise<MealLog> {
  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }
  const res = await apiClient.post<MealLog>(`/nutrition/meals/${mealId}/analyze-with-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function regenerateMeal(mealId: number, data: MealRegenerateRequest): Promise<MealLog> {
  const res = await apiClient.post<MealLog>(`/nutrition/meals/${mealId}/regenerate`, data)
  return res.data
}

// ==============================================================================
// Daily Summary
// ==============================================================================

export async function getDailySummary(date: string): Promise<DailyNutrition> {
  const res = await apiClient.get<DailyNutrition>('/nutrition/daily', {
    params: { target_date: date },
  })
  return res.data
}

export async function generateDailyAdvice(date: string): Promise<DailyNutrition> {
  const res = await apiClient.post<DailyNutrition>('/nutrition/daily/advice', null, {
    params: { target_date: date },
  })
  return res.data
}

// ==============================================================================
// Food Templates
// ==============================================================================

export async function listTemplates(sort?: TemplateSortOrder): Promise<FoodTemplate[]> {
  const res = await apiClient.get<FoodTemplate[]>('/nutrition/templates', {
    params: sort ? { sort } : undefined,
  })
  return res.data
}

export async function createTemplate(data: FoodTemplateCreate): Promise<FoodTemplate> {
  const res = await apiClient.post<FoodTemplate>('/nutrition/templates', data)
  return res.data
}

export async function createTemplateWithImage(file: File, text: string, name: string | null): Promise<FoodTemplate> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('text', text)
  if (name) formData.append('name', name)
  const res = await apiClient.post<FoodTemplate>('/nutrition/templates/with-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function createTemplateFromMealItems(data: FoodTemplateFromMealItems): Promise<FoodTemplate> {
  const res = await apiClient.post<FoodTemplate>('/nutrition/templates/from-meal-items', data)
  return res.data
}

export async function getMealItemHistory(q: string, limit: number, offset: number): Promise<MealItemHistoryPage> {
  const res = await apiClient.get<MealItemHistoryPage>('/nutrition/meal-items/history', {
    params: { q: q || undefined, limit, offset },
  })
  return res.data
}

export async function deleteTemplate(templateId: number): Promise<void> {
  await apiClient.delete(`/nutrition/templates/${templateId}`)
}

export async function regenerateTemplate(templateId: number, data: FoodTemplateRegenerate): Promise<FoodTemplate> {
  const res = await apiClient.post<FoodTemplate>(`/nutrition/templates/${templateId}/regenerate`, data)
  return res.data
}

// ==============================================================================
// Meal Plan
// ==============================================================================

export async function getMealPlan(targetDate: string): Promise<MealPlanRead | null> {
  const res = await apiClient.get<MealPlanRead | null>('/nutrition/plan', { params: { target_date: targetDate } })
  return res.data
}

export async function createMealPlan(data: MealPlanCreate): Promise<MealPlanRead> {
  const res = await apiClient.post<MealPlanRead>('/nutrition/plan', data)
  return res.data
}

export async function suggestMealPlan(data: MealPlanSuggestRequest): Promise<MealPlanSuggestion[]> {
  const res = await apiClient.post<MealPlanSuggestion[]>('/nutrition/plan/suggest', data)
  return res.data
}

export async function deleteMealPlan(planId: number): Promise<void> {
  await apiClient.delete(`/nutrition/plan/${planId}`)
}

// ==============================================================================
// 食品検索 (kalori.jp)
// ==============================================================================

export async function searchFoods(
  query: string,
  limit = 20,
  shops?: string | null,
  offset = 0,
): Promise<FoodSearchPage> {
  const params: Record<string, string | number> = { q: query, limit, offset }
  if (shops) params.shops = shops
  const res = await apiClient.get<FoodSearchPage>('/nutrition/foods/search', {
    params,
  })
  return res.data
}

export async function listKaloriShops(): Promise<KaloriShopList> {
  const res = await apiClient.get<KaloriShopList>('/nutrition/shops')
  return res.data
}

export async function parseFoodText(text?: string, file?: File): Promise<AiMealAnalysisResponse> {
  const formData = new FormData()
  if (text) formData.append('text', text)
  if (file) formData.append('file', file)
  const res = await apiClient.post<AiMealAnalysisResponse>('/nutrition/foods/parse', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}
